// ─── I dati della cassa ──────────────────────────────────────────────────────
//
// ⚖️ Lo stato del server non si duplica: TanStack Query è l'unica copia. Vale
// per i soldi più che per tutto il resto — due copie dello stesso totale
// divergono, e quando divergono nessuno se ne accorge finché qualcuno non conta
// il cassetto.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ElencoIncassi,
  EsitoIncasso,
  EsitoIncassoInBlocco,
  IncassoInBlocco,
  NuovoIncasso,
  RiepilogoCassa,
} from '@pmo/shared';
import { chiama, scarica } from '../../lib/api.js';
import { inEuro } from '../../lib/esiti.js';

export interface FiltroCassa {
  from: string;
  to: string;
  method?: string;
  memberId?: string;
  includiStornati: boolean;
}

function parametri(f: FiltroCassa): URLSearchParams {
  const p = new URLSearchParams({ from: f.from, to: f.to });
  if (f.method) p.set('method', f.method);
  if (f.memberId) p.set('memberId', f.memberId);
  if (!f.includiStornati) p.set('includiStornati', 'false');
  return p;
}

export const usaIncassi = (f: FiltroCassa) =>
  useQuery({
    queryKey: ['incassi', f.from, f.to, f.method ?? '', f.memberId ?? '', f.includiStornati],
    queryFn: () => chiama<ElencoIncassi>('GET', `/payments?${parametri(f).toString()}`),
    placeholderData: (prima) => prima,
  });

export const usaRiepilogo = (from: string, to: string) =>
  useQuery({
    queryKey: ['riepilogo-cassa', from, to],
    queryFn: () => chiama<RiepilogoCassa>('GET', `/payments/summary?from=${from}&to=${to}`),
    placeholderData: (prima) => prima,
  });

/**
 * Dopo ogni movimento di cassa si rilegge tutto ciò che quel movimento tocca.
 *
 * 🚨 La scheda della prenotazione è nell'elenco, e non è un di più: dentro c'è
 * **quanto è già stato incassato** e il saldo dei borsellini. Lasciandola
 * ferma, il dialogo riproporrebbe di incassare una cifra già presa.
 */
function rileggiLaCassa(qc: ReturnType<typeof useQueryClient>, bookingId?: string | null): void {
  void qc.invalidateQueries({ queryKey: ['incassi'] });
  void qc.invalidateQueries({ queryKey: ['riepilogo-cassa'] });
  void qc.invalidateQueries({ queryKey: ['borsellino'] });
  void qc.invalidateQueries({ queryKey: ['soci'] });
  void qc.invalidateQueries({ queryKey: ['attivita-socio'] });
  if (bookingId) void qc.invalidateQueries({ queryKey: ['prenotazione', bookingId] });
}

export function usaIncasso(bookingId?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    meta: {
      // ⚖️ `creato: false` è la stessa chiave già vista: l'incasso c'era, e non si conta due volte.
      fatto: (d) => {
        const e = d as EsitoIncasso;
        return e.creato
          ? `incasso di ${inEuro(e.payment.amountCents)} registrato`
          : 'questo incasso era già registrato, e non l’ho contato due volte';
      },
    },
    mutationFn: (i: NuovoIncasso) => chiama<EsitoIncasso>('POST', '/payments', i),
    onSuccess: () => rileggiLaCassa(qc, bookingId),
  });
}

/**
 * ⭐⭐ **QUATTRO CHE PAGANO INSIEME SONO UNA RICHIESTA SOLA**, e non quattro in
 * fila: se una riga viene rifiutata non ne resta scritta nessuna.
 *
 * ⚖️ Con quattro `mutate` di fila, al primo rifiuto due incassi sarebbero
 * passati e due no, e la schermata non saprebbe dire **quali**: chi sta al banco
 * dovrebbe ricostruirlo contando le righe in cassa. Il server lo fa in una
 * transazione — qui basta non disfarla mandando le righe separate.
 */
export function usaIncassoInBlocco(bookingId: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: {
      fatto: (d) => {
        const esiti = (d as EsitoIncassoInBlocco).esiti;
        const nuovi = esiti.filter((e) => e.creato);
        const totale = inEuro(nuovi.reduce((s, e) => s + e.payment.amountCents, 0));
        const gia = esiti.length - nuovi.length;
        const detto =
          nuovi.length === 1
            ? `1 incasso registrato (${totale})`
            : `${nuovi.length} incassi registrati (${totale})`;
        return gia === 0
          ? detto
          : `${detto}; ${gia === 1 ? '1 era già registrato' : `${gia} erano già registrati`}`;
      },
    },
    mutationFn: (i: IncassoInBlocco) => chiama<EsitoIncassoInBlocco>('POST', '/payments/batch', i),
    onSuccess: () => rileggiLaCassa(qc, bookingId),
  });
}

export function usaStornoIncasso(bookingId?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    meta: { fatto: 'incasso stornato' },
    mutationFn: (v: { id: string; reason: string | null }) =>
      chiama<EsitoIncasso>('POST', `/payments/${v.id}/void`, { reason: v.reason }),
    onSuccess: () => rileggiLaCassa(qc, bookingId),
  });
}

/**
 * ⛔ Non è un `<a href>`: il gettone sta in memoria, non in un cookie, quindi
 * un download avviato dal browser tornerebbe un 401.
 */
/**
 * ⭐⭐ **IL FILE È LA COPIA DELL'ELENCO CHE SI STA GUARDANDO.**
 *
 * 🚨 E fino al 18/09/2026 non lo era: qui si mandavano periodo e mezzo, e
 * `includiStornati` restava indietro — la rotta lo forzava a `true`. ⇒ Togliendo
 * la spunta «anche gli storni» l'elenco passava da 5 righe a 3 e **il file ne
 * conteneva 5 lo stesso**, senza che niente lo dicesse.
 * 🗣️ *«Mi sa il flag sugli storni non funziona»* — e funzionava, ma su un pezzo
 *   di schermata su tre.
 * 📌 *Il filtro si passa TUTTO o non si passa: mandarne una parte è peggio che
 *    non mandarne nessuna, perché il file sembra quello giusto.*
 */
export function esportaCassa(formato: 'csv' | 'xlsx', f: FiltroCassa): Promise<void> {
  const p = new URLSearchParams({
    format: formato,
    from: f.from,
    to: f.to,
    includiStornati: String(f.includiStornati),
  });
  if (f.method) p.set('method', f.method);
  return scarica(`/payments/export?${p.toString()}`, `cassa.${formato}`);
}
